'use client'
import React, { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import { AiFillHeart, AiOutlineHeart } from 'react-icons/ai'
import { sendLike } from '@services/sendLike'
import LinkComponent from '@components/LinkComponent'
import { sendGAEvent } from '@next/third-parties/google'
import { getCookie } from 'cookies-next'
import { currencies } from '@constants/index' // adjust this path to wherever currencies.js actually lives in your project

// ─── Default labels ────────────────────────────────────────────────────────────
const DEFAULT_LABELS = {
  month: {
    en: 'month', th: 'เดือน', cn: '月', ru: 'мес', fr: 'mois', de: 'Mon.', it: 'mese',
    ja: '月', ko: '월', es: 'mes', tr: 'ay', he: 'חודש', ar: 'شهر',
  },
  studio: {
    en: 'Studio', th: 'สตูดิโอ', cn: '开间', ru: 'Студия', fr: 'Studio', de: 'Studio',
    it: 'Monolocale', ja: 'スタジオ', ko: '스튜디오', es: 'Estudio', tr: 'Stüdyo', he: 'סטודיו', ar: 'استوديو',
  },
  sqm: {
    en: 'm²', th: 'ตร.ม.', cn: '平米', ru: 'м²', fr: 'm²', de: 'm²', it: 'm²',
    ja: '㎡', ko: '㎡', es: 'm²', tr: 'm²', he: 'מ״ר', ar: 'م²',
  },
  save: {
    en: 'Save',   th: 'บันทึก',    cn: '收藏',    ru: 'Сохранить',  fr: 'Sauver',     de: 'Merken',      it: 'Salva',
    ja: '保存',   ko: '저장',      es: 'Guardar', tr: 'Kaydet',     he: 'שמור',       ar: 'حفظ',
  },
  saved: {
    en: 'Saved',  th: 'บันทึกแล้ว', cn: '已收藏',  ru: 'Сохранено',  fr: 'Sauvegardé', de: 'Gespeichert', it: 'Salvato',
    ja: '保存済', ko: '저장됨',     es: 'Guardado', tr: 'Kaydedildi', he: 'נשמר',        ar: 'محفوظ',
  },
}

// ─── Currency symbol lookup ────────────────────────────────────────────────────
// Resolves the display symbol for the active currency (e.g. from the `currency`
// cookie the nav's CurrencySelector writes) against the site's own `currencies`
// data — the single source of truth for value → symbol — rather than any
// hardcoded map or CMS field config. `lang` is used to pick the matching locale
// list, but falls back to 'en' (the symbol itself doesn't vary by language).
function getCurrencySymbol(currencyValue, lang = 'en') {
  const list = currencies[lang] ?? currencies.en
  const match = list?.find(c => c.value === String(currencyValue ?? '').toLowerCase())
  if (match) return match.symbol
  // Currency not in the list (or none provided yet) — fall back to THB, since
  // that's the site's base/default currency.
  const fallback = (currencies.en ?? []).find(c => c.value === 'thb')
  return fallback?.symbol ?? '฿'
}

// ─── Inline SVG icons ─────────────────────────────────────────────────────────
const SvgIcons = {
  none: null,
  bed: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9V4a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v5"/><path d="M2 20v-5a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v5"/><path d="M2 20h20"/><path d="M2 9h20"/><rect x="6" y="9" width="4" height="5"/><rect x="14" y="9" width="4" height="5"/></svg>,
  bath: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.683 3 4 3.683 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"/><line x1="10" y1="5" x2="8" y2="7"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="7" y1="19" x2="7" y2="21"/><line x1="17" y1="19" x2="17" y2="21"/></svg>,
  area: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>,
  land: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 20h18"/><path d="m7 20-4-8"/><path d="m17 20 4-8"/><path d="m9 12 3-8 3 8"/><path d="M9 12h6"/></svg>,
  location: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>,
  price: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  building: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>,
  tag: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"/></svg>,
  star: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  ruler: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"/><path d="m14.5 12.5 2-2"/><path d="m11.5 9.5 2-2"/><path d="m8.5 6.5 2-2"/><path d="m17.5 15.5 2-2"/></svg>,
  video: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 8-6 4 6 4V8z"/><rect x="2" y="6" width="14" height="12" rx="2"/></svg>,
  cube: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
  waves: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/></svg>,
  mountain: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m8 3 4 8 5-5 5 15H2L8 3z"/></svg>,
  transit: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="16" rx="2"/><path d="M4 11h16"/><path d="M12 3v8"/><path d="M8 19l-2 3"/><path d="M18 22l-2-3"/><path d="M8 15h0"/><path d="M16 15h0"/></svg>,
  paw: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="4" r="2"/><circle cx="18" cy="8" r="2"/><circle cx="4" cy="8" r="2"/><path d="M9.3 14a5 5 0 0 0-2.5 4.4V21h8.4v-2.6a5 5 0 0 0-2.5-4.4 3.7 3.7 0 0 0-3.4 0z"/><circle cx="18" cy="14" r="2"/></svg>,
  camera: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>,
  phone: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.59 3.5 2 2 0 0 1 3.56 1.3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.08 6.08l1.98-1.98a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  mail: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>,
  listing: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h16v14H4z"/><path d="M8 9h8M8 13h5"/></svg>,
  house: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 11 9-8 9 8v10H3z"/><path d="M9 21v-7h6v7"/></svg>,
  map: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 7 5-4 6 3 7-3v14l-7 3-6-3-5 3z"/><path d="M8 3v14M14 6v14"/></svg>,
}

// ─── Spec label translations ───────────────────────────────────────────────────
const SPEC_LABELS = {
  en: { beds: 'Beds', bath: 'Baths', living: 'Living', land: 'Land' },
  th: { beds: 'ห้องนอน', bath: 'ห้องน้ำ', living: 'พื้นที่', land: 'ที่ดิน' },
  cn: { beds: '卧室', bath: '浴室', living: '面积', land: '土地' },
  ru: { beds: 'Спальни', bath: 'Ванные', living: 'Площадь', land: 'Участок' },
  de: { beds: 'Schlafz.', bath: 'Bäder', living: 'Wohnfl.', land: 'Grund' },
  fr: { beds: 'Chambres', bath: 'Salles', living: 'Surface', land: 'Terrain' },
  it: { beds: 'Camere', bath: 'Bagni', living: 'Superficie', land: 'Terreno' },
  ja: { beds: '寝室', bath: 'バス', living: '床面積', land: '土地' },
  ko: { beds: '침실', bath: '욕실', living: '생활면적', land: '토지' },
  tr: { beds: 'Yatak', bath: 'Banyo', living: 'Alan', land: 'Arazi' },
  he: { beds: 'חדרי שינה', bath: 'חדרי רחצה', living: 'שטח מגורים', land: 'קרקע' },
  ar: { beds: 'غرف نوم', bath: 'حمامات', living: 'مساحة', land: 'أرض' },
  es: { beds: 'Hab.', bath: 'Baños', living: 'Superficie', land: 'Terreno' },
}
const getSpecLabels = (lang) => SPEC_LABELS[lang] ?? SPEC_LABELS.en

// ─── Card extensions: status label, category chip, spec grid, link field, text style ──
// Shared logic: this block is identical in LargePropertyCard.jsx and
// PropertyCardEditor.jsx (editor preview). Keep the two copies in sync.
const EXT_LABELS = {
  forSale:     { en: 'For sale', th: 'ขาย', cn: '出售', ru: 'Продажа', de: 'Zu verkaufen', it: 'In vendita', fr: 'À vendre', es: 'En venta', tr: 'Satılık', ko: '매매', ja: '売り', ar: 'للبيع', he: 'למכירה' },
  forRent:     { en: 'For rent', th: 'เช่า', cn: '出租', ru: 'Аренда', de: 'Zu vermieten', it: 'In affitto', fr: 'À louer', es: 'En alquiler', tr: 'Kiralık', ko: '임대', ja: '賃貸', ar: 'للإيجار', he: 'להשכרה' },
  sale:        { en: 'Sale', th: 'ขาย', cn: '出售', ru: 'Продажа', de: 'Kauf', it: 'Vendita', fr: 'Vente', es: 'Venta', tr: 'Satış', ko: '매매', ja: '売買', ar: 'بيع', he: 'מכירה' },
  rent:        { en: 'Rent', th: 'เช่า', cn: '出租', ru: 'Аренда', de: 'Miete', it: 'Affitto', fr: 'Location', es: 'Alquiler', tr: 'Kira', ko: '임대', ja: '賃貸', ar: 'إيجار', he: 'שכירות' },
  notListed:   { en: 'Not listed', th: 'ยังไม่ระบุ', cn: '未注明', ru: 'Не указано', de: 'Keine Angabe', it: 'Non indicato', fr: 'Non précisé', es: 'Sin indicar', tr: 'Belirtilmemiş', ko: '미기재', ja: '未記載', ar: 'غير مذكور', he: 'לא צוין' },
  viewDetails: { en: 'View property details', th: 'ดูรายละเอียดทรัพย์', cn: '查看房源详情', ru: 'Подробнее об объекте', de: 'Details ansehen', it: 'Vedi dettagli', fr: 'Voir le bien', es: 'Ver detalles', tr: 'Detayları gör', ko: '매물 상세보기', ja: '物件詳細を見る', ar: 'عرض تفاصيل العقار', he: 'לפרטי הנכס' },
  ref:         { en: 'Listing no.', th: 'รหัสทรัพย์', cn: '房源编号', ru: 'Номер объекта', de: 'Objekt-Nr.', it: 'Rif. annuncio', fr: 'Réf. annonce', es: 'Ref. anuncio', tr: 'İlan no.', ko: '매물 번호', ja: '物件番号', ar: 'رقم الإعلان', he: 'מס׳ נכס' },
  price:       { en: 'Price', th: 'ราคา', cn: '价格', ru: 'Цена', de: 'Preis', it: 'Prezzo', fr: 'Prix', es: 'Precio', tr: 'Fiyat', ko: '가격', ja: '価格', ar: 'السعر', he: 'מחיר' },
  land:        { en: 'Land size', th: 'ขนาดที่ดิน', cn: '土地面积', ru: 'Участок', de: 'Grundstück', it: 'Terreno', fr: 'Terrain', es: 'Parcela', tr: 'Arsa', ko: '대지 면적', ja: '土地面積', ar: 'مساحة الأرض', he: 'שטח מגרש' },
  living:      { en: 'House size', th: 'พื้นที่ใช้สอย', cn: '使用面积', ru: 'Площадь дома', de: 'Wohnfläche', it: 'Superficie', fr: 'Surface', es: 'Superficie', tr: 'Ev alanı', ko: '전용 면적', ja: '建物面積', ar: 'مساحة المنزل', he: 'שטח הבית' },
  beds:        { en: 'Bed', th: 'ห้องนอน', cn: '卧室', ru: 'Спальни', de: 'Schlafz.', it: 'Camere', fr: 'Ch.', es: 'Dorm.', tr: 'Yatak', ko: '침실', ja: '寝室', ar: 'غرف النوم', he: 'חדרים' },
  bath:        { en: 'Bath', th: 'ห้องน้ำ', cn: '浴室', ru: 'Ванные', de: 'Bad', it: 'Bagni', fr: 'SdB', es: 'Baños', tr: 'Banyo', ko: '욕실', ja: 'バス', ar: 'الحمامات', he: 'אמבטיות' },
}
const extT = (key, lang = 'en', override) =>
  (override && (override[lang] || override.en)) || EXT_LABELS[key]?.[lang] || EXT_LABELS[key]?.en || ''

// Optional per-field text styling: brand type role (font family etc. from the
// site's brand CSS classes), text transform and letter spacing.
const extTextStyle = (field = {}) => ({
  ...(field.textTransform && field.textTransform !== 'none' ? { textTransform: field.textTransform } : {}),
  ...(field.letterSpacing ? { letterSpacing: `${field.letterSpacing}em` } : {}),
})
const extTypeClass = (field = {}) => (field.typeRole && field.typeRole !== 'none' ? `type-${field.typeRole}` : undefined)

// Spec grid: which items it can hold and their default icons.
const SPEC_GRID_KEYS = ['ref', 'price', 'land', 'living', 'beds', 'bath']
const SPEC_GRID_ICONS = { ref: 'listing', price: 'price', land: 'map', living: 'house', beds: 'bed', bath: 'bath' }

// Icon at any size/stroke (the SvgIcons set is drawn at 14px).
const sizedIcon = (name, size = 14, stroke = 2) =>
  SvgIcons[name] ? React.cloneElement(SvgIcons[name], { width: size, height: size, strokeWidth: stroke }) : null

// ─── RTL / align helpers ───────────────────────────────────────────────────────
const ALIGN_MAP = { left: 'start', right: 'end', center: 'center', start: 'start', end: 'end' }
const normalizeAlign = (align) => ALIGN_MAP[align] ?? 'start'
const toJustify    = (align) => align === 'center' ? 'center' : align === 'end' ? 'flex-end'  : 'flex-start'
const toAlignItems = (align) => align === 'center' ? 'center' : align === 'end' ? 'flex-end'  : 'flex-start'

// ─── Badge chip style ──────────────────────────────────────────────────────────
function badgeChipStyle(b, onImage = false) {
  const variant = b.variant ?? 'filled', radius = b.radius ?? 20
  const fontSize = b.fontSize ?? 0.65, fontWeight = b.fontWeight ?? '600'
  let bg, color, border
  if (variant === 'filled')        { bg = onImage ? `${b.color}dd` : b.color; color = '#fff'; border = 'none' }
  else if (variant === 'outlined') { bg = 'transparent'; color = b.color; border = `1.5px solid ${b.color}` }
  else                             { bg = `${b.color}20`; color = b.color; border = `1px solid ${b.color}35` }
  return { display: 'inline-flex', alignItems: 'center', gap: 3, background: bg, color, border, fontSize: `${fontSize}rem`, fontWeight, padding: '3px 8px', borderRadius: radius, backdropFilter: onImage ? 'blur(4px)' : undefined, lineHeight: 1.4 }
}

// ─── Tags chip style + block ────────────────────────────────────────────────────
function tagChipStyle(field) {
  const variant = field.variant ?? 'subtle', radius = field.radius ?? 12
  const fontSize = field.fontSize ?? 0.7, fontWeight = field.fontWeight ?? '600'
  const color = field.color ?? '#374151'
  let bg, textColor, border
  if (variant === 'filled')        { bg = color; textColor = '#fff'; border = 'none' }
  else if (variant === 'outlined') { bg = 'transparent'; textColor = color; border = `1.5px solid ${color}` }
  else                              { bg = field.bg && field.bg !== 'transparent' ? field.bg : `${color}14`; textColor = color; border = `1px solid ${color}25` }
  return { display: 'inline-flex', alignItems: 'center', gap: 3, background: bg, color: textColor, border, fontSize: `${fontSize}rem`, fontWeight, padding: '3px 9px', borderRadius: radius, lineHeight: 1.4 }
}

function TagsBlock({ field, compact }) {
  const tags = Array.isArray(field.value) ? field.value : []
  if (!tags.length) return null
  if (field.displayType === 'text') {
    return (
      <div style={{ fontSize: `${field.fontSize ?? 0.7}rem`, fontWeight: field.fontWeight ?? '600', color: field.color ?? '#374151', marginBottom: compact ? 0 : 4 }}>
        {tags.join(' · ')}
      </div>
    )
  }
  const style = tagChipStyle(field)
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: compact ? 0 : 4 }}>
      {tags.map((t, i) => (
        <span key={i} style={compact ? { ...style, backdropFilter: 'blur(4px)' } : style}>
          {field.icon && field.icon !== 'none' && SvgIcons[field.icon]}
          {t}
        </span>
      ))}
    </div>
  )
}

// ─── Label helpers ─────────────────────────────────────────────────────────────
function labelBg(field) {
  if ((field.bgType ?? 'solid') === 'gradient') return field.bgGradient ?? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)'
  return field.bg ?? '#2563eb'
}
function labelBorderRadius(field, brandRadiusMd) {
  const v = brandRadiusMd ?? '0.375rem'
  const brandPx = String(v).endsWith('rem') ? Math.round(parseFloat(v) * 16) : (parseInt(v) || 6)
  const tl = field.radiusTL ?? brandPx, tr = field.radiusTR ?? brandPx
  const br = field.radiusBR ?? brandPx, bl = field.radiusBL ?? brandPx
  return (field.radiusLinked !== false) ? tl : `${tl}px ${tr}px ${br}px ${bl}px`
}
function labelStyle(field, brandRadiusMd) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: labelBg(field), color: field.color ?? '#ffffff',
    fontSize: `${field.fontSize ?? 0.75}rem`, fontWeight: field.fontWeight ?? '600',
    padding: `${field.paddingY ?? 3}px ${field.paddingX ?? 8}px`,
    border: (field.borderWidth ?? 0) > 0 ? `${field.borderWidth}px solid ${field.borderColor ?? '#ffffff'}` : 'none',
    borderRadius: labelBorderRadius(field, brandRadiusMd),
    lineHeight: 1.4,
    ...extTextStyle(field),
  }
}
function labelPositionStyle(field) {
  const ox = field.positionOffsetX ?? 10, oy = field.positionOffsetY ?? 10
  const base = { position: 'absolute', display: 'flex', flexDirection: 'column', gap: 4, zIndex: 2 }
  switch (field.position) {
    case 'top-left':      return { ...base, top: oy, insetInlineStart: ox }
    case 'top-center':    return { ...base, top: oy, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' }
    case 'top-right':     return { ...base, top: oy, insetInlineEnd: ox, alignItems: 'flex-end' }
    case 'bottom-left':   return { ...base, bottom: oy, insetInlineStart: ox }
    case 'bottom-center': return { ...base, bottom: oy, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' }
    case 'bottom-right':  return { ...base, bottom: oy, insetInlineEnd: ox, alignItems: 'flex-end' }
    default:              return { ...base, top: oy, insetInlineStart: ox }
  }
}

// ─── Spec item ─────────────────────────────────────────────────────────────────
function SpecItem({ icon, val, label, color, size, fw, block, showIcon = true }) {
  if (!val && val !== 0) return null
  return (
    <div style={{ display: block ? 'flex' : 'inline-flex', alignItems: 'center', gap: 4 }}>
      {showIcon && <span style={{ color, opacity: 0.7, flexShrink: 0, display: 'flex' }}>{SvgIcons[icon]}</span>}
      <span style={{ fontSize: `${size}rem`, color, fontWeight: fw ?? '500' }}>{val}</span>
      {label && <span style={{ fontSize: `${size * 0.9}rem`, color, opacity: 0.6 }}>{label}</span>}
    </div>
  )
}

// ─── Transit block ─────────────────────────────────────────────────────────────
const TRANSIT_LINES = [
  { id: 'bts_sukhumvit', abbr: 'BTS', color: '#7db71f', textColor: '#fff' },
  { id: 'bts_silom',     abbr: 'BTS', color: '#007a3d', textColor: '#fff' },
  { id: 'mrt_blue',      abbr: 'MRT', color: '#1a3e8f', textColor: '#fff' },
  { id: 'mrt_purple',    abbr: 'MRT', color: '#7b2d8b', textColor: '#fff' },
  { id: 'arl',           abbr: 'ARL', color: '#e0251b', textColor: '#fff' },
  { id: 'brt',           abbr: 'BRT', color: '#f37021', textColor: '#fff' },
  { id: 'mrt_yellow',    abbr: 'MRT', color: '#f5c400', textColor: '#000' },
  { id: 'mrt_pink',      abbr: 'MRT', color: '#e4007f', textColor: '#fff' },
]
function TransitBlock({ field, compact }) {
  const lines = (field.transitLines ?? []).filter(tl => tl.lineId)
  if (!lines.length) return null
  const displayType = field.displayType ?? 'badge', variant = field.variant ?? 'filled', showIcon = field.showIcon ?? true
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: compact ? 0 : 4 }}>
      {lines.map((tl, i) => {
        const brand = TRANSIT_LINES.find(l => l.id === tl.lineId) ?? TRANSIT_LINES[0]
        const chipLabel = `${brand.abbr}${tl.station ? ` · ${tl.station}` : ''}${tl.distance ? ` ${tl.distance}` : ''}`
        if (displayType === 'text') return (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: `${field.fontSize}rem`, fontWeight: field.fontWeight ?? '600', color: brand.color }}>
            {showIcon && <span style={{ display: 'flex', flexShrink: 0 }}>{SvgIcons.transit}</span>}
            {chipLabel}
          </span>
        )
        let bgColor, textColor, border
        if (variant === 'filled')        { bgColor = brand.color; textColor = brand.textColor; border = 'none' }
        else if (variant === 'outlined') { bgColor = 'transparent'; textColor = brand.color; border = `1.5px solid ${brand.color}` }
        else                             { bgColor = `${brand.color}18`; textColor = brand.color; border = `1px solid ${brand.color}30` }
        return (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: bgColor, color: textColor, border, fontSize: `${field.fontSize}rem`, fontWeight: field.fontWeight ?? '600', padding: compact ? '2px 6px' : '3px 8px', borderRadius: field.radius ?? 20, backdropFilter: compact ? 'blur(4px)' : undefined, lineHeight: 1.4 }}>
            {showIcon && <span style={{ display: 'flex', flexShrink: 0, opacity: 0.9 }}>{SvgIcons.transit}</span>}
            {chipLabel}
          </span>
        )
      })}
    </div>
  )
}

function RefChip({ field, value, onImage = false }) {
  const displayType = field.displayType ?? 'text', color = field.badgeColor ?? field.color ?? '#6b7280'
  const fontSize = field.fontSize ?? 0.7, fontWeight = field.fontWeight ?? '500', radius = field.radius ?? 4
  if (displayType === 'badge') {
    const variant = field.variant ?? 'outlined'
    let bg, textColor, border
    if (variant === 'filled')        { bg = color; textColor = '#fff'; border = 'none' }
    else if (variant === 'outlined') { bg = onImage ? 'rgba(0,0,0,0.3)' : 'transparent'; textColor = onImage ? '#fff' : color; border = `1.5px solid ${onImage ? 'rgba(255,255,255,0.7)' : color}` }
    else                             { bg = `${color}20`; textColor = color; border = `1px solid ${color}35` }
    return <span style={{ display: 'inline-flex', alignItems: 'center', background: bg, color: textColor, border, fontSize: `${fontSize}rem`, fontWeight, padding: '2px 8px', borderRadius: radius, backdropFilter: onImage ? 'blur(4px)' : undefined, lineHeight: 1.4 }}>{value}</span>
  }
  return <span style={{ fontSize: `${fontSize}rem`, fontWeight, color: onImage ? 'rgba(255,255,255,0.85)' : (field.color ?? '#6b7280'), textShadow: onImage ? '0 1px 3px rgba(0,0,0,0.5)' : undefined }}>{value}</span>
}

// ─── Image overlay layer ───────────────────────────────────────────────────────
function CardImageOverlays({ fields, badges, card, brandRadiusMd }) {
  const enabledBadgesOnImage = badges.filter(b => b.enabled && b.showOnImage)
  const chipFields = fields.filter(f => (f.id === 'label' || f.id === 'category') && f.enabled && f.showOnImage && f.value)
  return (
    <>
      {chipFields.map(chip => (
        <div key={chip.id} style={labelPositionStyle(chip)}>
          <span className={extTypeClass(chip)} style={labelStyle(chip, brandRadiusMd)}>
            {chip.icon && chip.icon !== 'none' && SvgIcons[chip.icon]}
            {chip.value}
          </span>
        </div>
      ))}
      {enabledBadgesOnImage.length > 0 && (
        <div style={{ position: 'absolute', bottom: 10, insetInlineStart: 10, insetInlineEnd: 10, display: 'flex', flexWrap: 'wrap', gap: 4, zIndex: 2 }}>
          {enabledBadgesOnImage.map(b => (
            <span key={b.id} style={badgeChipStyle(b, true)}>{SvgIcons[b.icon]}{b.label}</span>
          ))}
        </div>
      )}
      {fields.filter(f => f.isTransit && f.enabled && f.showOnImage).map(field => (
        <div key={field.id} style={{ position: 'absolute', bottom: 10, insetInlineEnd: 10, display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-end', zIndex: 2 }}>
          <TransitBlock field={field} compact />
        </div>
      ))}
      {fields.filter(f => f.isRef && f.enabled && f.showOnImage).map(field => (
        <div key={field.id} style={labelPositionStyle(field)}>
          <RefChip field={field} value={field.value} onImage />
        </div>
      ))}
      {fields.filter(f => f.isTags && f.enabled && f.showOnImage).map(field => (
        <div key={field.id} style={{ position: 'absolute', bottom: 10, insetInlineStart: 10, insetInlineEnd: 10, zIndex: 2 }}>
          <TagsBlock field={field} compact />
        </div>
      ))}
    </>
  )
}

// ─── Gallery Slider ────────────────────────────────────────────────────────────
function GallerySlider({ images, alt, blur }) {
  const [index, setIndex] = useState(0)
  const [hovered, setHovered] = useState(false)
  const touchStart = useRef(null)
  const total = images?.length ?? 0
  if (!total) return null
  const prev = (e) => { e.preventDefault(); e.stopPropagation(); setIndex(i => (i - 1 + total) % total) }
  const next = (e) => { e.preventDefault(); e.stopPropagation(); setIndex(i => (i + 1) % total) }
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX }
  const onTouchEnd = (e) => {
    if (touchStart.current === null) return
    const delta = touchStart.current - e.changedTouches[0].clientX
    if (Math.abs(delta) > 40) delta > 0 ? setIndex(i => (i + 1) % total) : setIndex(i => (i - 1 + total) % total)
    touchStart.current = null
  }
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {images.map((src, i) => (
        <div key={i} style={{ position: 'absolute', inset: 0, opacity: i === index ? 1 : 0, transition: 'opacity 0.3s ease', pointerEvents: i === index ? 'auto' : 'none' }}>
          <Image placeholder={i === 0 && blur ? 'blur' : 'empty'} blurDataURL={i === 0 ? blur : undefined} src={src || '/images/imageplaceholder.jpeg'} fill alt={`${alt} ${i + 1}`} style={{ objectFit: 'cover' }} />
        </div>
      ))}
      {total > 1 && (
        <>
          <button onClick={prev} style={{ position: 'absolute', insetInlineStart: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 4, background: 'rgba(0,0,0,0.35)', border: 'none', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', backdropFilter: 'blur(4px)', padding: 0, opacity: hovered ? 1 : 0, transition: 'opacity 0.2s ease' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <button onClick={next} style={{ position: 'absolute', insetInlineEnd: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 4, background: 'rgba(0,0,0,0.35)', border: 'none', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', backdropFilter: 'blur(4px)', padding: 0, opacity: hovered ? 1 : 0, transition: 'opacity 0.2s ease' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
          </button>
          <div style={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 5, zIndex: 4, opacity: hovered ? 1 : 0, transition: 'opacity 0.2s ease' }}>
            {images.map((_, i) => <button key={i} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIndex(i) }} style={{ width: i === index ? 16 : 6, height: 6, borderRadius: 3, border: 'none', padding: 0, cursor: 'pointer', background: i === index ? '#fff' : 'rgba(255,255,255,0.5)', transition: 'all 0.2s ease' }} />)}
          </div>
          <div style={{ position: 'absolute', top: 8, insetInlineStart: 8, zIndex: 4, background: 'rgba(0,0,0,0.4)', color: '#fff', fontSize: '0.65rem', fontWeight: 600, padding: '2px 7px', borderRadius: 20, backdropFilter: 'blur(4px)', letterSpacing: '0.04em', opacity: hovered ? 1 : 0, transition: 'opacity 0.2s ease' }}>
            {index + 1} / {total}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Rightmove-style horizontal image panel ────────────────────────────────────
function RightmoveImagePanel({ property, card, fields, badges, brandRadiusMd, resolvedGallerySlider, resolvedSaleType, imageWidthPct, minCardHeight, includeInactive }) {
  const accentColor = card.rightmoveAccentColor ?? '#00737a'
  const gallery = property.gallery ?? []
  const mainSrc  = gallery[0] ?? property.cover ?? '/images/imageplaceholder.jpeg'
  const sub1Src  = gallery[1] ?? null
  const sub2Src  = gallery[2] ?? null
  const photoCount = gallery.length || 1

  const priceField = fields.find(f => f.id === 'price' && f.enabled)
  const sqmField   = fields.find(f => f.id === 'sqm'   && f.enabled)
  const labelField = fields.find(f => f.id === 'label' && f.enabled)
  const enabledBadgesOnImage = badges.filter(b => b.enabled && b.showOnImage)

  return (
    <div style={{ position: 'relative', width: `${imageWidthPct}%`, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: `${card.radius ?? 0}px 0 0 ${card.radius ?? 0}px` }}>
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: sub1Src ? '3fr 2fr' : '1fr', gridTemplateRows: sub1Src ? '1fr 1fr' : '1fr', position: 'relative', minHeight: minCardHeight - 52 }}>
        <div style={{ gridRow: '1 / 3', position: 'relative', overflow: 'hidden' }}>
          {resolvedGallerySlider && gallery.length > 0
            ? <GallerySlider images={gallery} alt={property.slug ?? property.name} blur={property.blur} />
            : <Image placeholder={property.blur ? 'blur' : 'empty'} blurDataURL={property.blur} src={mainSrc} fill alt={property.slug ?? property.name} style={{ objectFit: 'cover' }} />
          }
          {includeInactive && (property.listingStatus !== 'active' || property.active === false) && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 }}>
              <span style={{ color: '#fff', fontSize: '2rem', fontWeight: 700 }}>{resolvedSaleType === 'sale' ? 'SOLD' : 'RENTED'}</span>
            </div>
          )}
        </div>
        {sub1Src && (
          <div style={{ position: 'relative', overflow: 'hidden', borderLeft: '2px solid #fff', borderBottom: '1px solid #fff' }}>
            <Image src={sub1Src} fill alt="" style={{ objectFit: 'cover' }} />
          </div>
        )}
        {sub2Src && (
          <div style={{ position: 'relative', overflow: 'hidden', borderLeft: '2px solid #fff', borderTop: '1px solid #fff' }}>
            <Image src={sub2Src} fill alt="" style={{ objectFit: 'cover' }} />
            {gallery.length > 3 && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.9rem', fontWeight: 700, gap: 5 }}>
                +{gallery.length - 3} more
              </div>
            )}
          </div>
        )}
        <div style={{ position: 'absolute', top: 10, insetInlineStart: 10, zIndex: 4, display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '4px 9px', borderRadius: 4, backdropFilter: 'blur(4px)', letterSpacing: '0.04em' }}>
          <span style={{ display: 'flex', opacity: 0.9 }}>{SvgIcons.camera}</span>
          {photoCount}/{photoCount}
        </div>
        {labelField?.showOnImage && (
          <div style={labelPositionStyle(labelField)}>
            <span style={labelStyle(labelField, brandRadiusMd)}>
              {labelField.icon !== 'none' && SvgIcons[labelField.icon]}
              {labelField.value}
            </span>
          </div>
        )}
        {enabledBadgesOnImage.length > 0 && (
          <div style={{ position: 'absolute', bottom: 60, insetInlineStart: 10, display: 'flex', flexWrap: 'wrap', gap: 4, zIndex: 2 }}>
            {enabledBadgesOnImage.map(b => (
              <span key={b.id} style={badgeChipStyle(b, true)}>{SvgIcons[b.icon]}{b.label}</span>
            ))}
          </div>
        )}
      </div>
      <div style={{ background: accentColor, padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
        {priceField && priceField.value && (
          <span style={{ fontSize: `${priceField.fontSize ?? 1.25}rem`, fontWeight: priceField.fontWeight ?? '700', color: '#ffffff', lineHeight: 1.2 }}>
            {priceField.value}
          </span>
        )}
        {sqmField && sqmField.value && (
          <span style={{ fontSize: `${sqmField.fontSize ?? 0.75}rem`, fontWeight: '400', color: 'rgba(255,255,255,0.8)', lineHeight: 1.3 }}>
            {sqmField.value}
          </span>
        )}
      </div>
    </div>
  )
}

// ─── Field data hydration ──────────────────────────────────────────────────────
// NOTE: `currency` (the active currency code, e.g. from getCookie('currency'))
// is now accepted so the price/sqm prefix reflects what's actually selected
// instead of always defaulting to THB's ฿.
function hydrateFields(fields, property, { lang = 'en', dictionary = {}, saleType, cardType, currency } = {}) {
  const resolvedSaleType = saleType || property.metaSaleType || (property.salePrice ? 'sale' : 'rent')
  const district = property.districts?.[0]
  const districtName = district?.subdistrictName?.[lang] ?? district?.subdistrict
  const locationStr = [districtName, property.location].filter(Boolean).join(', ')
  const metaSale = String(property.metaSaleType ?? '').toLowerCase()
  const statusKey = metaSale.includes('rent') && !metaSale.includes('sale') ? 'forRent'
                  : (metaSale.includes('sale') || property.salePrice) ? 'forSale'
                  : (property.rentPrice ? 'forRent' : null)
  const isLand = property.metaCategory === 'Land'
  const isStudio = property.subcategory?.toLowerCase() === 'studio'
  const monthLabel  = dictionary.month  ?? DEFAULT_LABELS.month[lang]  ?? DEFAULT_LABELS.month.en
  const studioLabel = dictionary.studio ?? DEFAULT_LABELS.studio[lang] ?? DEFAULT_LABELS.studio.en
  const sqmLabel    = dictionary.sqm    ?? DEFAULT_LABELS.sqm[lang]    ?? DEFAULT_LABELS.sqm.en
  const bedsVal  = isStudio ? studioLabel : (property.bedrooms  > 0 ? String(property.bedrooms)  : null)
  const bathVal  = property.bathrooms > 0 ? String(property.bathrooms) : null
  const livingVal = property.livingArea > 0 ? `${property.livingArea} ${sqmLabel}` : null
  const landRaw   = property.landArea ?? property.landSize
  const landVal   = landRaw > 0 ? `${landRaw} ${sqmLabel}` : null
  // Resolve once: the currency symbol actually in effect for this render,
  // looked up from the site's `currencies` constant (not CMS field config).
  // `property.currency` (per-property, if the API returns it) wins over the
  // page-level `currency` option, which wins over the THB fallback.
  const activeCurrencySymbol = getCurrencySymbol(property.currency ?? currency, lang)

  return fields.map(field => {
    if (field.isDivider) return field
    switch (field.id) {
      case 'label': {
        // source: 'promotion' (default — previous behaviour) | 'status' (For sale / For rent) | 'statusOrPromotion'
        const source = field.source ?? 'promotion'
        const status = statusKey ? extT(statusKey, lang, field.statusLabels?.[statusKey]) : ''
        if (cardType === 'new' && field.enabled) {
          const listedAt = property.listedAt ?? null
          const v = listedAt ?? (property.promotionTitle || (source !== 'promotion' ? status : ''))
          return { ...field, value: v, enabled: field.enabled && !!v }
        }
        const v = source === 'status' ? status
                : source === 'statusOrPromotion' ? (property.promotionTitle || status)
                : (property.promotionTitle || '')
        return { ...field, value: v, enabled: field.enabled && !!v }
      }
      case 'category': return { ...field, value: property.category ?? property.metaCategory ?? '' }
      case 'link':     return { ...field, value: extT('viewDetails', lang, typeof field.value === 'object' ? field.value : undefined) }
      case 'name':  return { ...field, value: property.name ?? '' }
      case 'price': {
        // Prefix always comes from the site's active currency — never from
        // CMS field config — so it stays in sync with what the user selected.
        const prefix = activeCurrencySymbol
        const rawPrice = resolvedSaleType === 'rent' && property.rentPrice
          ? `${property.rentPrice}/${monthLabel}`
          : property.salePrice ? String(property.salePrice) : ''
        return { ...field, value: rawPrice ? (prefix ? `${prefix} ${rawPrice}` : rawPrice) : '' }
      }
      case 'project':  return { ...field, value: property.projectName ?? property.project ?? '' }
      case 'location': {
        const mode = field.locationMode ?? 'both'
        const v = mode === 'district' ? (districtName || property.location || '')
                : mode === 'city'     ? (property.location || districtName || '')
                : locationStr
        return { ...field, value: v }
      }
      case 'sqm': {
        const prefix = activeCurrencySymbol
        const sqmPrice = property.salePrice && property.livingArea
          ? `${prefix} ${Math.round(property.salePrice / property.livingArea).toLocaleString()} / ${sqmLabel}`
          : ''
        return { ...field, value: sqmPrice }
      }
      case 'ref':   return { ...field, value: property.ref ?? property.referenceId ?? property.id ?? '' }
      case 'specs': {
        // Spec grid extras: listing no. + price can sit inside the grid.
        const saleP = property.salePrice ? `${activeCurrencySymbol} ${property.salePrice}` : ''
        const rentP = property.rentPrice ? `${activeCurrencySymbol} ${property.rentPrice}/${monthLabel}` : ''
        const word  = (k) => field.priceSaleWord === false ? '' : `${extT(k, lang)} `
        const priceVal = resolvedSaleType === 'rent' ? (rentP ? word('rent') + rentP : null)
                       : (saleP ? word('sale') + saleP : (rentP ? word('rent') + rentP : null))
        const grid = field.layout === 'grid' && Array.isArray(field.gridItems) && field.gridItems.length > 0
        return {
          ...field,
          value: {
            beds: isLand ? null : bedsVal, bath: isLand ? null : bathVal, living: livingVal, land: landVal ?? (isLand ? livingVal : null),
            ref: property.ref ?? property.referenceId ?? property.id ?? null, price: priceVal,
          },
          specLabels: getSpecLabels(lang),
          gridLabels: grid ? Object.fromEntries(SPEC_GRID_KEYS.map(k => [k, extT(k, lang, field.itemLabels?.[k])])) : undefined,
          emptyValue: field.showEmpty ? extT('notListed', lang, field.emptyText) : null,
        }
      }
      case 'transit':
        if (property.publicTransport?.length) {
          return { ...field, transitLines: property.publicTransport.map(t => ({ lineId: resolveTransitLineId(t), station: t.transportName?.[lang] ?? t.stationName ?? '', distance: t.distance ?? '' })) }
        }
        return { ...field, enabled: false, transitLines: [] }
      // ── New fields ──────────────────────────────────────────────────────────
      case 'description': return { ...field, value: property.description ?? property.shortDescription ?? '' }
      case 'agentNote':   return { ...field, value: property.agentNote ?? property.reducedNote ?? '' }
      case 'agentName':   return { ...field, value: property.agentName ?? property.agency ?? '' }
      case 'phone':       return { ...field, value: property.phone ?? property.agentPhone ?? '' }
      case 'tags':        return { ...field, value: Array.isArray(property.tags) ? property.tags : [] }
      default: return field
    }
  })
}

function resolveTransitLineId(transport) {
  const type = (transport.type ?? '').toLowerCase()
  const lineColor = (transport.lineColor ?? '').toLowerCase()
  const color = (transport.colorCode ?? '').toLowerCase()
  if (type === 'brt') return 'brt'
  if (type === 'arl') return 'arl'
  if (type === 'bts') return lineColor.includes('dark') ? 'bts_silom' : 'bts_sukhumvit'
  if (type === 'mrt') {
    if (lineColor.includes('purple')) return 'mrt_purple'
    if (lineColor.includes('yellow')) return 'mrt_yellow'
    if (lineColor.includes('pink'))   return 'mrt_pink'
    if (color.includes('7b2'))        return 'mrt_purple'
    return 'mrt_blue'
  }
  return 'mrt_blue'
}

function hydrateBadges(badges, property, lang = 'en') {
  const flagMap = { seaview: !!property.isSeaView, beachfront: !!property.isBeachfront, petfriendly: !!property.isPetFriendly, foreignquota: !!property.foreignQuota, mountainview: !!property.isMountainView, video: !!property.videoUrl, walkthrough: !!property.walkthroughUrl }
  return badges.map(b => ({ ...b, label: b.labels?.[lang] ?? b.label, enabled: b.enabled && (flagMap[b.id] ?? false) }))
}

// ─── Rightmove-style content panel ────────────────────────────────────────────
function RightmoveContentPanel({ hydratedFields, enabledBadgesInContent, card, property, isLiked, onLike, lang = 'en', dictionary = {} }) {
  const accentColor = card.rightmoveAccentColor ?? '#00737a'
  const saveLabel  = dictionary.save  ?? DEFAULT_LABELS.save[lang]  ?? DEFAULT_LABELS.save.en
  const savedLabel = dictionary.saved ?? DEFAULT_LABELS.saved[lang] ?? DEFAULT_LABELS.saved.en
  const locationField   = hydratedFields.find(f => f.id === 'location')
  const nameField       = hydratedFields.find(f => f.id === 'name')
  const descField       = hydratedFields.find(f => f.id === 'description')
  const agentNoteField  = hydratedFields.find(f => f.id === 'agentNote')
  const agentNameField  = hydratedFields.find(f => f.id === 'agentName')
  const phoneField      = hydratedFields.find(f => f.id === 'phone')
  const specsField      = hydratedFields.find(f => f.isSpecs && f.enabled)
  const transitFields   = hydratedFields.filter(f => f.isTransit && f.enabled && !f.showOnImage)
  const tagsField       = hydratedFields.find(f => f.isTags && f.enabled && !f.showOnImage)

  const padding = card.contentPadding ?? 16

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding, overflow: 'hidden' }}>
      {/* Location breadcrumb */}
      {locationField?.value && (
        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ display: 'flex', color: '#9ca3af' }}>{SvgIcons.location}</span>
          {locationField.value}
        </div>
      )}

      {/* Property name / title */}
      {nameField?.value && (
        <h3 style={{ margin: '0 0 6px 0', fontSize: `${nameField.fontSize ?? 1.0625}rem`, fontWeight: nameField.fontWeight ?? '700', color: nameField.color ?? '#111827', lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {nameField.value}
        </h3>
      )}

      {/* Specs (beds/bath/area) */}
      {specsField && (
        <RightmoveSpecs field={specsField} />
      )}

      {/* Description excerpt */}
      {descField?.enabled && descField.value && (
        <p style={{ margin: '8px 0 0', fontSize: `${descField.fontSize ?? 0.8125}rem`, color: descField.color ?? '#4b5563', lineHeight: 1.55, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', flex: 1 }}>
          {descField.value}
        </p>
      )}

      {/* Transit badges */}
      {transitFields.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {transitFields.map(f => <TransitBlock key={f.id} field={f} />)}
        </div>
      )}

      {/* Tags */}
      {tagsField && Array.isArray(tagsField.value) && tagsField.value.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <TagsBlock field={tagsField} />
        </div>
      )}

      {/* In-content badges */}
      {enabledBadgesInContent.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
          {enabledBadgesInContent.map(b => <span key={b.id} style={badgeChipStyle(b, false)}>{SvgIcons[b.icon]}{b.label}</span>)}
        </div>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Agent note */}
      {agentNoteField?.enabled && agentNoteField.value && (
        <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic', lineHeight: 1.4 }}>
          {agentNoteField.value}
        </p>
      )}

      {/* Footer: agent + phone + actions */}
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent:'flex-end', gap: 12, flexWrap: 'wrap' }}>
        {/* Save button — wired to like state */}
        <button
          onClick={onLike}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 6, border: `1.5px solid ${isLiked ? '#fca5a5' : '#e5e7eb'}`, color: isLiked ? '#ef4444' : '#374151', background: isLiked ? '#fff5f5' : 'transparent', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, flexShrink: 0, transition: 'all 0.15s ease' }}>
          {isLiked
            ? <AiFillHeart size={14} color="#ef4444" />
            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
          }
          {isLiked ? savedLabel : saveLabel}
        </button>
      </div>
    </div>
  )
}

function RightmoveSpecs({ field }) {
  const sl = field.specLabels ?? SPEC_LABELS.en
  const items = [
    { icon: 'bed',  val: field.value?.beds,   label: sl.beds   },
    { icon: 'bath', val: field.value?.bath,   label: sl.bath   },
    { icon: 'area', val: field.value?.living, label: sl.living },
    ...(field.showLand !== false ? [{ icon: 'land', val: field.value?.land, label: sl.land }] : []),
  ].filter(it => it.val != null)
  if (!items.length) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '4px 0' }}>
      {items.map((it, i) => (
        <span key={it.icon} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: `${field.fontSize ?? 0.8125}rem`, color: field.color ?? '#374151', fontWeight: field.fontWeight ?? '500' }}>
          {i > 0 && <span style={{ color: '#d1d5db', marginInlineEnd: 4 }}>·</span>}
          {field.showIcons !== false && <span style={{ opacity: 0.6 }}>{SvgIcons[it.icon]}</span>}
          <span>{it.val}</span>
          {field.showLabels !== false && <span style={{ opacity: 0.55, fontSize: `${(field.fontSize ?? 0.8125) * 0.88}rem` }}>{it.label}</span>}
        </span>
      ))}
    </div>
  )
}

// ─── Standard content renderer (vertical / non-rightmove horizontal) ──────────
function CardContent({ hydratedFields, enabledBadgesInContent, card }) {
  const isHorizontal = (card.orientation ?? 'vertical') === 'horizontal'
  return (
    <>
      {(() => {
      // Content badges go just above the first field pinned to the bottom (or at the end).
      const list = hydratedFields.filter(f => (f.enabled && f.id !== 'label' && !(f.id === 'category' && f.showOnImage)) || f.isDivider)
      const pin = list.findIndex(f => f.pinBottom)
      const badgesEl = enabledBadgesInContent.length > 0 ? (
        <div key="__content-badges" style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
          {enabledBadgesInContent.map(b => <span key={b.id} style={badgeChipStyle(b, false)}>{SvgIcons[b.icon]}{b.label}</span>)}
        </div>
      ) : null
      const renderField = (field) => {
        if (field.isDivider) {
          const lineStyle = { marginTop: field.marginTop ?? 6, marginBottom: field.marginBottom ?? 6, borderTop: `${field.thickness ?? 1}px ${field.style ?? 'solid'} ${field.color ?? '#e5e7eb'}` }
          // pinBottom: pushes this divider (and every field after it) to the bottom of an equal-height card
          return field.pinBottom
            ? <div key={field.id} style={{ marginTop: 'auto', paddingTop: field.marginTop ?? 6 }}><div style={{ ...lineStyle, marginTop: 0 }} /></div>
            : <div key={field.id} style={lineStyle} />
        }
        if (field.id === 'name') {
          const clamp = field.lineClamp ?? 2, lineH = 1.35
          const height = (!isHorizontal && field.fixedHeight) ? `${clamp * lineH * field.fontSize}rem` : undefined
          const align = normalizeAlign(field.align)
          return (
            <div key={field.id} style={{ textAlign: align, marginBottom: 4, background: field.bg !== 'transparent' ? field.bg : undefined, padding: field.bg !== 'transparent' ? '2px 6px' : 0, borderRadius: field.bg !== 'transparent' ? 4 : 0, height, overflow: 'hidden' }}>
              <span className={extTypeClass(field)} style={{ display: '-webkit-box', WebkitLineClamp: clamp, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: `${field.fontSize}rem`, fontWeight: field.fontWeight, color: field.color, lineHeight: lineH, ...extTextStyle(field) }}>
                {field.icon && field.icon !== 'none' && <span style={{ opacity: 0.7, marginInlineEnd: 5 }}>{SvgIcons[field.icon]}</span>}
                {field.value}
              </span>
            </div>
          )
        }
        if (field.isSpecs) {
          const layout = field.layout ?? 'row', showLabels = field.showLabels ?? true, showIcons = field.showIcons ?? true
          const align = normalizeAlign(field.align), sl = field.specLabels ?? SPEC_LABELS.en
          const allItems = [
            { icon: 'bed',  val: field.value?.beds,   label: sl.beds   },
            { icon: 'bath', val: field.value?.bath,   label: sl.bath   },
            { icon: 'area', val: field.value?.living, label: sl.living },
            ...(field.showLand !== false ? [{ icon: 'land', val: field.value?.land, label: sl.land }] : []),
          ]
          const items = allItems.filter(it => it.val != null)
          const invisible = !items.length
          const displayItems = invisible ? allItems.map(it => ({ ...it, val: '\u00A0', label: '' })) : items
          if (layout === 'row') return (
            <div key={field.id} style={{ textAlign: align, marginBottom: 6, visibility: invisible ? 'hidden' : 'visible', overflow: 'hidden' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: isHorizontal ? 6 : 10, flexWrap: isHorizontal ? 'nowrap' : 'wrap', maxWidth: '100%' }}>
                {displayItems.map((it, i) => (
                  <span key={it.icon} style={{ display: 'inline-flex', alignItems: 'center', flexShrink: isHorizontal ? 1 : 0, minWidth: 0 }}>
                    {i > 0 && <span style={{ color: '#e5e7eb', marginInlineEnd: isHorizontal ? 6 : 10, flexShrink: 0 }}>|</span>}
                    <SpecItem icon={it.icon} val={it.val} label={showLabels && !isHorizontal ? it.label : ''} color={field.color} size={field.fontSize} fw={field.fontWeight} showIcon={showIcons} />
                  </span>
                ))}
              </div>
            </div>
          )
          if (layout === 'grid' && Array.isArray(field.gridItems) && field.gridItems.length) {
            const cols = field.gridColumns ?? 2, top = (field.labelPosition ?? 'inline') === 'top'
            const iconSz = field.iconSize ?? 18
            const gridItems = field.gridItems.filter(k => SPEC_GRID_KEYS.includes(k)).map(k => ({
              key: k, icon: field.itemIcons?.[k] ?? SPEC_GRID_ICONS[k],
              val: field.value?.[k] ?? field.emptyValue, label: field.gridLabels?.[k] ?? k,
            })).filter(it => it.val != null && it.val !== '')
            return (
              <div key={field.id} style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: `${field.rowGap ?? 14}px ${field.columnGap ?? 10}px`, marginBottom: 6 }}>
                {gridItems.map(it => (
                  <div key={it.key} style={{ display: 'flex', alignItems: top ? 'flex-start' : 'center', gap: 8, minWidth: 0 }}>
                    {showIcons && <span style={{ display: 'flex', flexShrink: 0, color: field.iconColor ?? field.color, marginTop: top ? 1 : 0 }}>{sizedIcon(it.icon, iconSz, 1.55)}</span>}
                    {top ? (
                      <span style={{ display: 'block', minWidth: 0 }}>
                        {showLabels && <small style={{ display: 'block', fontSize: `${field.labelFontSize ?? 0.75}rem`, color: field.labelColor ?? field.color, opacity: field.labelColor ? 1 : 0.6, lineHeight: 1.4, marginBottom: 2 }}>{it.label}</small>}
                        <strong style={{ display: 'block', fontSize: `${field.fontSize}rem`, fontWeight: field.fontWeight, color: field.color, lineHeight: 1.4, overflowWrap: 'anywhere' }}>{it.val}</strong>
                      </span>
                    ) : (
                      <span style={{ fontSize: `${field.fontSize}rem`, fontWeight: field.fontWeight, color: field.color }}>
                        {it.val}{showLabels && <span style={{ marginInlineStart: 4, color: field.labelColor ?? field.color, opacity: field.labelColor ? 1 : 0.6, fontWeight: 400 }}>{it.label}</span>}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )
          }
          if (layout === 'grid') return (
            <div key={field.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px', marginBottom: 6, visibility: invisible ? 'hidden' : 'visible' }}>
              {displayItems.map(it => <SpecItem key={it.icon} icon={it.icon} val={it.val} label={showLabels ? it.label : ''} color={field.color} size={field.fontSize} fw={field.fontWeight} block showIcon={showIcons} />)}
            </div>
          )
          return (
            <div key={field.id} style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 6, alignItems: toAlignItems(align), visibility: invisible ? 'hidden' : 'visible' }}>
              {displayItems.map(it => <SpecItem key={it.icon} icon={it.icon} val={it.val} label={showLabels ? it.label : ''} color={field.color} size={field.fontSize} fw={field.fontWeight} showIcon={showIcons} />)}
            </div>
          )
        }
        if (field.isTransit) {
          if (field.showOnImage) return null
          const hasLines = (field.transitLines ?? []).length > 0
          return <div key={field.id} style={{ visibility: hasLines ? 'visible' : 'hidden' }}><TransitBlock field={field} /></div>
        }
        if (field.isTags) {
          if (field.showOnImage) return null
          const hasTags = Array.isArray(field.value) && field.value.length > 0
          return <div key={field.id} style={{ visibility: hasTags ? 'visible' : 'hidden' }}><TagsBlock field={field} /></div>
        }
        if (field.isRef) {
          if (field.showOnImage) return null
          const align = normalizeAlign(field.align ?? 'start')
          return (
            <div key={field.id} style={{ textAlign: align, marginBottom: 4, visibility: field.value ? 'visible' : 'hidden' }}>
              <RefChip field={field} value={field.value || '\u00A0'} />
            </div>
          )
        }
        if (field.id === 'link') {
          const align = normalizeAlign(field.align)
          return (
            <div key={field.id} style={{ textAlign: align, marginTop: field.pinBottom ? 'auto' : 0, paddingTop: field.pinBottom ? 4 : 0 }}>
              <span className={extTypeClass(field)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: `${field.fontSize}rem`, fontWeight: field.fontWeight, color: field.color, justifyContent: toJustify(align), ...extTextStyle(field) }}>
                {field.icon && field.icon !== 'none' && <span style={{ opacity: 0.7, flexShrink: 0 }}>{SvgIcons[field.icon]}</span>}
                {field.value}
                {field.showArrow && <span aria-hidden="true">→</span>}
              </span>
            </div>
          )
        }
        // Skip price/sqm in standard content when they're in the image bar (rightmove mode)
        if ((field.id === 'price' || field.id === 'sqm') && card.rightmoveStyle) return null
        const align = normalizeAlign(field.align)
        return (
          <div key={field.id} style={{ textAlign: align, marginBottom: 4, visibility: field.value ? 'visible' : 'hidden', background: field.bg !== 'transparent' ? field.bg : undefined, padding: field.bg !== 'transparent' ? '2px 6px' : 0, borderRadius: field.bg !== 'transparent' ? 4 : 0 }}>
            <span className={extTypeClass(field)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: `${field.fontSize}rem`, fontWeight: field.fontWeight, color: field.color, justifyContent: toJustify(align), ...extTextStyle(field) }}>
              {field.icon && field.icon !== 'none' && <span style={{ opacity: 0.7, flexShrink: 0 }}>{SvgIcons[field.icon]}</span>}
              {field.value || '\u00A0'}
            </span>
          </div>
        )
      }
      return pin < 0
        ? [...list.map(renderField), badgesEl]
        : [...list.slice(0, pin).map(renderField), badgesEl, ...list.slice(pin).map(renderField)]
      })()}
    </>
  )
}

// ─── Main LargePropertyCard ────────────────────────────────────────────────────
const LargePropertyCard = ({
  property,
  cardStyle,
  gallerySlider   = false,
  imageWidth      = 300,
  imageHeight     = 200,
  includeInactive = false,
  newLimit,
  isLast,
  district        = undefined,
  dictionary      = {},
  lang            = 'en',
  saleType,
  cardType,
  brandRadiusMd,
  currency,        // NEW: explicit currency code prop (e.g. from a server component reading the cookie)
}) => {
  const [isLiked, setIsLiked] = useState(property.isLiked)
  const cardRef = useRef()
  useEffect(() => {
    if (!cardRef?.current || !isLast) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { newLimit?.(); observer.unobserve(entry.target) }
    })
    observer.observe(cardRef.current)
    return () => observer.disconnect()
  }, [isLast])

  // Fallback: if the parent didn't pass `currency` explicitly (e.g. this card
  // is rendered client-side without threading the prop through), read the
  // same `currency` cookie the nav's CurrencySelector writes to, so the
  // symbol still matches what the user picked even without prop-drilling.
  const [resolvedCurrency, setResolvedCurrency] = useState(currency)
  useEffect(() => {
    if (currency) { setResolvedCurrency(currency); return }
    const cookieCurrency = getCookie('currency')
    if (cookieCurrency) setResolvedCurrency(cookieCurrency)
  }, [currency])

  // Listen for the nav's currency switch so the symbol updates immediately —
  // router.refresh() alone re-renders server components with fresh prices,
  // but this is a client component and won't otherwise notice the cookie
  // changed until something remounts it (hence the "needs a refresh" bug).
  useEffect(() => {
    if (currency) return // an explicit prop always wins; nothing to listen for
    const handler = (e) => {
      const next = e?.detail?.currency
      if (next) setResolvedCurrency(next)
    }
    window.addEventListener('currencychange', handler)
    return () => window.removeEventListener('currencychange', handler)
  }, [currency])

  const handleLike = async (e) => {
    e.preventDefault(); e.stopPropagation()
    await sendLike(property.id)
    setIsLiked(prev => !prev)
    property.isLiked = !property.isLiked
  }

  const hasStyle = cardStyle?.fields && cardStyle?.badges && cardStyle?.card
  if (!hasStyle) {
    return <LegacyCard property={property} imageWidth={imageWidth} imageHeight={imageHeight} includeInactive={includeInactive} newLimit={newLimit} isLast={isLast} district={district} dictionary={dictionary} lang={lang} saleType={saleType} currency={resolvedCurrency} />
  }

  const { card } = cardStyle
  const isHorizontal = (card.orientation ?? 'vertical') === 'horizontal'
  const isRightmove  = isHorizontal && (card.rightmoveStyle ?? false)
  const imageWidthPct = card.imageWidth ?? 38
  const minCardHeight = card.minHeight  ?? (isRightmove ? 240 : 160)
  const resolvedGallerySlider = gallerySlider || (card.gallerySlider ?? false)
  const hydratedFields = hydrateFields(cardStyle.fields, property, { lang, dictionary, saleType, cardType, currency: resolvedCurrency })
  const hydratedBadges = hydrateBadges(cardStyle.badges, property, lang)
  const enabledBadgesInContent = hydratedBadges.filter(b => b.enabled && !b.showOnImage)
  const resolvedSaleType = saleType || property.metaSaleType || (property.salePrice ? 'sale' : 'rent')

  // ── Rightmove layout — responsive: desktop = horizontal grid, mobile = vertical price-bar ──
  if (isRightmove) {
    const accentColor    = card.rightmoveAccentColor        ?? '#00737a'
    const showActionBar  = card.mobileListingShowActionBar  ?? true
    const actionBarBg    = card.mobileListingActionBarBg    ?? '#ffffff'
    const actionBarColor = card.mobileListingActionBarColor ?? '#374151'

    const priceField     = hydratedFields.find(f => f.id === 'price'    && f.enabled)
    const sqmField       = hydratedFields.find(f => f.id === 'sqm'      && f.enabled)
    const labelField     = hydratedFields.find(f => f.id === 'label'    && f.enabled)
    const nameField      = hydratedFields.find(f => f.id === 'name')
    const locationField  = hydratedFields.find(f => f.id === 'location')
    const agentNoteField = hydratedFields.find(f => f.id === 'agentNote' && f.enabled)
    const agentNameField = hydratedFields.find(f => f.id === 'agentName' && f.enabled)
    const specsField     = hydratedFields.find(f => f.isSpecs && f.enabled)
    const tagsField      = hydratedFields.find(f => f.isTags && f.enabled && !f.showOnImage)
    const enabledBadgesOnImage = hydratedBadges.filter(b => b.enabled && b.showOnImage)
    const phoneValue  = hydratedFields.find(f => f.id === 'phone')?.value ?? ''
    const gaPayload   = { property_id: property.ref || property.id, property_name: property?.name, property_type: property?.metaCategory, property_saletype: property?.metaSaleType }
    const saveLabel   = dictionary.save  ?? DEFAULT_LABELS.save[lang]  ?? DEFAULT_LABELS.save.en
    const savedLabel  = dictionary.saved ?? DEFAULT_LABELS.saved[lang] ?? DEFAULT_LABELS.saved.en

    // Inline SVGs for action bar (avoid importing extra deps)
    const iconPhone   = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.59 3.5 2 2 0 0 1 3.56 1.3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.08 6.08l1.98-1.98a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
    const iconMail    = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
    const iconHeart   = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
    const iconCamera  = <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>

    const uid = `rm-${property.id ?? property.slug ?? 'card'}`

    return (
      <div ref={cardRef} className={uid} style={{ width: '100%', background: card.bg, borderRadius: card.radius, border: card.borderWidth > 0 ? `${card.borderWidth}px solid ${card.borderColor}` : 'none', boxShadow: card.shadow, overflow: 'hidden', position: 'relative', cursor: 'pointer' }}
        onClick={() => sendGAEvent('event', 'select_property', gaPayload)}>

        {/* ─── Responsive styles ─────────────────────────────────────────── */}
        <style>{`
          .${uid} .rm-desktop { display: flex; }
          .${uid} .rm-mobile  { display: none; }
          @media (max-width: 639px) {
            .${uid} .rm-desktop { display: none; }
            .${uid} .rm-mobile  { display: flex; }
          }
        `}</style>

        {/* ══════════════════════════════════════════════════════════════════
            DESKTOP — horizontal image-grid layout (≥640px)
        ══════════════════════════════════════════════════════════════════ */}
        <LinkComponent href={`/properties/${property.slug}`} className="rm-desktop"
          style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <RightmoveImagePanel
            property={property}
            card={card}
            fields={hydratedFields}
            badges={hydratedBadges}
            brandRadiusMd={brandRadiusMd}
            resolvedGallerySlider={resolvedGallerySlider}
            resolvedSaleType={resolvedSaleType}
            imageWidthPct={imageWidthPct}
            minCardHeight={minCardHeight}
            includeInactive={includeInactive}
          />
          <RightmoveContentPanel
            hydratedFields={hydratedFields}
            enabledBadgesInContent={enabledBadgesInContent}
            card={card}
            property={property}
            isLiked={isLiked}
            onLike={handleLike}
            lang={lang}
            dictionary={dictionary}
          />
        </LinkComponent>

        {/* ══════════════════════════════════════════════════════════════════
            MOBILE — vertical price-bar-over-image layout (<640px)
        ══════════════════════════════════════════════════════════════════ */}
        <LinkComponent href={`/properties/${property.slug}`} className="rm-mobile"
          style={{ flexDirection: 'column', flex: 1, minWidth: 0 }}>

          {/* Image + teal price bar */}
          <div style={{ position: 'relative', aspectRatio: card.imageRatio ?? '4/3' }}>
            {resolvedGallerySlider && property.gallery?.length > 0 ? (
              <GallerySlider images={property.gallery} alt={property.slug ?? property.name} blur={property.blur} />
            ) : (
              <Image placeholder={property.blur ? 'blur' : 'empty'} blurDataURL={property.blur}
                src={property.cover || '/images/imageplaceholder.jpeg'} fill
                alt={property.slug ?? property.name} style={{ objectFit: 'cover' }} />
            )}
            {includeInactive && (property.listingStatus !== 'active' || property.active === false) && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 }}>
                <span style={{ color: '#fff', fontSize: '2rem', fontWeight: 700 }}>
                  {resolvedSaleType === 'sale' ? 'SOLD' : 'RENTED'}
                </span>
              </div>
            )}
            {/* Camera counter */}
            {(property.gallery?.length ?? 0) > 0 && (
              <div style={{ position: 'absolute', top: 10, insetInlineStart: 10, zIndex: 3, display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(15,23,42,0.72)', color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '4px 9px', borderRadius: 5, backdropFilter: 'blur(4px)', letterSpacing: '0.03em' }}>
                {iconCamera} 1/{property.gallery.length}
              </div>
            )}
            {/* Image badges */}
            {enabledBadgesOnImage.length > 0 && (
              <div style={{ position: 'absolute', bottom: 64, insetInlineStart: 10, display: 'flex', flexWrap: 'wrap', gap: 4, zIndex: 2 }}>
                {enabledBadgesOnImage.map(b => <span key={b.id} style={badgeChipStyle(b, true)}>{SvgIcons[b.icon]}{b.label}</span>)}
              </div>
            )}
            {/* Price bar pinned to bottom of image */}
            <div style={{ position: 'absolute', bottom: 0, insetInlineStart: 0, insetInlineEnd: 0, zIndex: 3, background: accentColor, padding: '10px 14px 12px' }}>
              {labelField?.value && (
                <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.72)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                  {labelField.value}
                </div>
              )}
              {priceField?.value && (
                <div style={{ fontSize: `${priceField.fontSize ?? 1.375}rem`, fontWeight: priceField.fontWeight ?? '700', color: '#fff', lineHeight: 1.15 }}>
                  {priceField.value}
                </div>
              )}
              {sqmField?.enabled && sqmField.value && (
                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)', fontWeight: '400', marginTop: 2 }}>
                  {sqmField.value}
                </div>
              )}
            </div>
          </div>

          {/* Content area */}
          <div style={{ padding: card.contentPadding ?? 16, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 4 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {locationField?.value && (
                  <div style={{ fontSize: `${locationField.fontSize ?? 0.875}rem`, fontWeight: locationField.fontWeight ?? '700', color: locationField.color ?? '#111827', lineHeight: 1.35, marginBottom: 3 }}>
                    {locationField.value}
                  </div>
                )}
                {nameField?.value && (
                  <div style={{ fontSize: `${nameField.fontSize ?? 0.875}rem`, fontWeight: nameField.fontWeight ?? '400', color: nameField.color ?? '#374151', lineHeight: 1.35, marginBottom: 2 }}>
                    {nameField.value}
                  </div>
                )}
              </div>
              {agentNameField?.enabled && agentNameField.value && (
                <div style={{ flexShrink: 0, textAlign: 'end', maxWidth: 90 }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                    {agentNameField.value}
                  </div>
                  <div style={{ fontSize: '0.55rem', color: '#9ca3af', lineHeight: 1.3, marginTop: 1 }}>
                    International Real Estate
                  </div>
                </div>
              )}
            </div>
            {specsField && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: `${specsField.fontSize ?? 0.8125}rem`, color: specsField.color ?? '#374151', fontWeight: specsField.fontWeight ?? '500', marginBottom: 2 }}>
                {specsField.value?.beds && <><span style={{ opacity: 0.6, display: 'flex' }}>{SvgIcons.bed}</span><span>{specsField.value.beds}</span>{specsField.showLabels !== false && <span style={{ opacity: 0.5, fontSize: '0.7rem' }}>{specsField.specLabels?.beds ?? 'Beds'}</span>}<span style={{ color: '#d1d5db' }}>·</span></>}
                {specsField.value?.bath && <><span style={{ opacity: 0.6, display: 'flex' }}>{SvgIcons.bath}</span><span>{specsField.value.bath}</span>{specsField.showLabels !== false && <span style={{ opacity: 0.5, fontSize: '0.7rem' }}>{specsField.specLabels?.bath ?? 'Baths'}</span>}<span style={{ color: '#d1d5db' }}>·</span></>}
                {specsField.value?.living && <><span style={{ opacity: 0.6, display: 'flex' }}>{SvgIcons.area}</span><span>{specsField.value.living}</span></>}
              </div>
            )}
            {agentNoteField?.enabled && agentNoteField.value && (
              <div style={{ fontSize: `${agentNoteField.fontSize ?? 0.8}rem`, fontStyle: 'italic', color: agentNoteField.color ?? accentColor, lineHeight: 1.4, marginTop: 2 }}>
                {agentNoteField.value}
              </div>
            )}
            {tagsField && Array.isArray(tagsField.value) && tagsField.value.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <TagsBlock field={tagsField} compact />
              </div>
            )}
            {enabledBadgesInContent.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                {enabledBadgesInContent.map(b => <span key={b.id} style={badgeChipStyle(b, false)}>{SvgIcons[b.icon]}{b.label}</span>)}
              </div>
            )}
          </div>
        </LinkComponent>

        {/* Action bar — only shown in mobile layout */}
        {showActionBar && (
          <div className="rm-mobile" style={{ borderTop: '1px solid #f3f4f6', background: actionBarBg, flexDirection: 'row', alignItems: 'stretch' }}>
            <button onClick={handleLike}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '13px 4px', background: 'transparent', border: 'none', borderLeft: '1px solid #f3f4f6', color: isLiked ? '#ef4444' : actionBarColor, fontSize: '0.8125rem', fontWeight: '500', cursor: 'pointer', userSelect: 'none' }}>
              {isLiked
                ? <AiFillHeart size={15} color="#ef4444" />
                : <span style={{ display: 'flex', opacity: 0.72 }}>{iconHeart}</span>
              }
              {isLiked ? savedLabel : saveLabel}
            </button>
          </div>
        )}
      </div>
    )
  }

  // ── Standard horizontal layout ───────────────────────────────────────────────
  if (isHorizontal) {
    const imagePanel = (
      <div style={{ position: 'relative', flexShrink: 0, width: `${imageWidthPct}%`, height: minCardHeight, overflow: 'hidden', borderRadius: card.imageRadius ?? 0, alignSelf: 'stretch' }}>
        {resolvedGallerySlider && property.gallery?.length > 0 ? (
          <GallerySlider images={property.gallery} alt={property.slug ?? property.name} blur={property.blur} />
        ) : (
          <Image placeholder={property.blur ? 'blur' : 'empty'} blurDataURL={property.blur} src={property.cover || '/images/imageplaceholder.jpeg'} fill sizes={`${imageWidthPct}vw`} alt={property.slug ?? property.name} style={{ objectFit: 'cover' }} />
        )}
        {includeInactive && (property.listingStatus !== 'active' || property.active === false) && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 }}>
            <span style={{ color: '#fff', fontSize: '2rem', fontWeight: 700 }}>{resolvedSaleType === 'sale' ? 'SOLD' : 'RENTED'}</span>
          </div>
        )}
        <CardImageOverlays fields={hydratedFields} badges={hydratedBadges} card={card} brandRadiusMd={brandRadiusMd} />
      </div>
    )
    return (
      <div ref={cardRef} style={{ width: '100%', background: card.bg, borderRadius: card.radius, border: card.borderWidth > 0 ? `${card.borderWidth}px solid ${card.borderColor}` : 'none', boxShadow: card.shadow, overflow: 'hidden', position: 'relative', cursor: 'pointer', display: 'flex', flexDirection: 'row', height: minCardHeight }}
        onClick={() => sendGAEvent('event', 'select_property', { property_id: property.ref || property.id, property_name: property?.name, property_type: property?.metaCategory, property_saletype: property?.metaSaleType })}>
        <div title={isLiked ? 'Unlike this property' : 'Save this property'} onClick={handleLike} style={{ position: 'absolute', top: 10, insetInlineEnd: 10, zIndex: 10, cursor: 'pointer' }}>
          {isLiked ? <AiFillHeart size={24} color={card.likeColor ?? '#ef4444'} /> : <AiOutlineHeart size={24} color={card.likeIdleColor ?? '#ffffff'} style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />}
        </div>
        <LinkComponent href={`/properties/${property.slug}`} style={{ display: 'flex', flex: 1, minWidth: 0, overflow: 'hidden', height: minCardHeight }}>
          {imagePanel}
          <div style={{ padding: card.contentPadding, overflow: 'hidden', flex: 1, minWidth: 0, height: minCardHeight, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
            <CardContent hydratedFields={hydratedFields} enabledBadgesInContent={enabledBadgesInContent} card={card} />
          </div>
        </LinkComponent>
      </div>
    )
  }

  // ── Vertical layout ──────────────────────────────────────────────────────────
  const imagePanel = (
    <div style={{ position: 'relative', aspectRatio: card.imageRatio ?? '4/3', overflow: 'hidden', borderRadius: card.imageRadius ?? 0 }}>
      {resolvedGallerySlider && property.gallery?.length > 0 ? (
        <GallerySlider images={property.gallery} alt={property.slug ?? property.name} blur={property.blur} />
      ) : (
        <Image placeholder={property.blur ? 'blur' : 'empty'} blurDataURL={property.blur} src={property.cover || '/images/imageplaceholder.jpeg'} fill sizes={`${imageWidth}px`} alt={property.slug ?? property.name} style={{ objectFit: 'cover' }} />
      )}
      {includeInactive && (property.listingStatus !== 'active' || property.active === false) && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 }}>
          <span style={{ color: '#fff', fontSize: '2rem', fontWeight: 700 }}>{resolvedSaleType === 'sale' ? 'SOLD' : 'RENTED'}</span>
        </div>
      )}
      <CardImageOverlays fields={hydratedFields} badges={hydratedBadges} card={card} brandRadiusMd={brandRadiusMd} />
    </div>
  )

  // equalHeight: card fills its grid cell and the content column stretches, so a
  // field with pinBottom (e.g. the link) lines up across a row of cards.
  const eq = card.equalHeight ?? false
  return (
    <div ref={cardRef} style={{ width: '100%', background: card.bg, borderRadius: card.radius, border: card.borderWidth > 0 ? `${card.borderWidth}px solid ${card.borderColor}` : 'none', boxShadow: card.shadow, overflow: 'hidden', position: 'relative', cursor: 'pointer', ...(eq ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}) }}
      onClick={() => sendGAEvent('event', 'select_property', { property_id: property.ref || property.id, property_name: property?.name, property_type: property?.metaCategory, property_saletype: property?.metaSaleType })}>
      {(card.showLike ?? true) && <div title={isLiked ? 'Unlike this property' : 'Save this property'} onClick={handleLike} style={{ position: 'absolute', top: 10, insetInlineEnd: 10, zIndex: 10, cursor: 'pointer' }}>
        {isLiked ? <AiFillHeart size={24} color={card.likeColor ?? '#ef4444'} /> : <AiOutlineHeart size={24} color={card.likeIdleColor ?? '#ffffff'} style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />}
      </div>}
      <LinkComponent href={`/properties/${property.slug}`} style={eq ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : undefined}>
        {imagePanel}
        <div style={{ padding: card.contentPadding, ...(eq ? { flex: 1, display: 'flex', flexDirection: 'column' } : {}) }}>
          <CardContent hydratedFields={hydratedFields} enabledBadgesInContent={enabledBadgesInContent} card={card} />
        </div>
      </LinkComponent>
    </div>
  )
}

// ─── Legacy fallback ───────────────────────────────────────────────────────────
function LegacyCard({ property, imageWidth, imageHeight, includeInactive, newLimit, isLast, district, dictionary, lang, saleType, currency }) {
  const [isLiked, setIsLiked] = useState(property.isLiked)
  const cardRef = useRef()
  useEffect(() => {
    if (!cardRef?.current || !isLast) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { newLimit?.(); observer.unobserve(entry.target) }
    })
    observer.observe(cardRef.current)
    return () => observer.disconnect()
  }, [isLast])
  const handleLike = async (e) => { e.preventDefault(); e.stopPropagation(); await sendLike(property.id); setIsLiked(prev => !prev); property.isLiked = !property.isLiked }
  const resolvedSaleType = saleType || (property.salePrice ? 'sale' : 'rent')
  const monthLabel = dictionary.month ?? DEFAULT_LABELS.month[lang] ?? DEFAULT_LABELS.month.en
  return (
    <div ref={cardRef} className="relative" style={{ width: imageWidth }} onClick={() => sendGAEvent('event', 'select_property', { property_id: property.ref || property.id, property_name: property?.name, property_type: property?.metaCategory, property_saletype: property?.metaSaleType })}>
      <div title={isLiked ? 'Unlike' : 'Save'} onClick={handleLike} className={isLiked ? 'card-liked' : 'card-unliked'}>
        {isLiked ? <AiFillHeart size={24} color="#ef4444" /> : <AiOutlineHeart size={24} />}
      </div>
      <LinkComponent href={`/properties/${property.slug}`}>
        <div>
          <div className="relative">
            <Image placeholder={property.blur ? 'blur' : 'empty'} blurDataURL={property.blur} className="large-card-image" src={property.cover || '/images/imageplaceholder.jpeg'} width={imageWidth} height={imageHeight} alt={property.slug} />
            {property.promotionTitle && <div className="label-highlight">{property.promotionTitle}</div>}
            {includeInactive && (property.listingStatus !== 'active' || property.active === false) && (
              <div className="absolute top-0 left-0 right-0 bottom-0 bg-maincolor opacity-50 z-10 rounded-md flex justify-center items-center">
                <span className="text-light text-6xl">{resolvedSaleType === 'sale' ? 'SOLD' : 'RENTED'}</span>
              </div>
            )}
            {property.publicTransport?.map((transport, i) => (
              <div key={i} style={{ backgroundColor: transport.colorCode }} className="transport-label">
                {transport.type.toUpperCase()} {transport.transportName?.[lang] ?? transport.stationName}
              </div>
            ))}
          </div>
          <div style={{ width: imageWidth }}>
            <div className="text-xl font-semibold text-maincolor sm:overflow-hidden sm:h-[70px] h-full"><h2 className="text-base">{property.name}</h2></div>
            {resolvedSaleType === 'sale' && property.salePrice && <div className="text-lg font-semibold text-price">{property.salePrice}</div>}
            {resolvedSaleType === 'rent' && property.rentPrice && <div className="text-lg font-semibold text-price">{property.rentPrice}/{monthLabel}</div>}
            <div className="flex flex-row gap-2 text-sm text-maincolor">
              {property.bedrooms  > 0 && <span>{property.bedrooms} bd</span>}
              {property.bathrooms > 0 && <span>{property.bathrooms} ba</span>}
              {property.livingArea > 0 && <span>{property.livingArea} m²</span>}
            </div>
            <div>
              <p className="text-mainhover">
                {district
                  ? `${district.subdistrictName?.[lang] ?? district.subdistrict}, ${property.location}`
                  : `${property.districts?.[0]?.subdistrictName?.[lang] ?? property.districts?.[0]?.subdistrict}, ${property.location}`}
              </p>
            </div>
          </div>
        </div>
      </LinkComponent>
    </div>
  )
}

export default LargePropertyCard